import * as React from 'react'
import { useAngularServices, SignatureAndSketch } from '@/react/components'
import { SingleSignature } from './index'
import { css } from '@emotion/react'
import { AddSignatureIcon2 } from '@/react/componentAssets'
import { CurrentUserType } from '@/react/types/documentation'
import { SignatureType } from '@/react/types'

interface SignatureElementProps {
  signatures: SignatureType[]
  setSignatures: (signatures: SignatureType[]) => void
}

export function SignatureElement({
  signatures,
  setSignatures,
}: SignatureElementProps) {
  const { Api, CurrentUser } = useAngularServices()
  const variableColor = (CurrentUser as CurrentUserType).getClientSettings()
    .web_primary_color

  const [modalState, setModalState] = React.useState<React.ReactElement[]>([])
  const [signatureElementList, setSignatureElementList] = React.useState<
    React.ReactElement[]
  >([])

  async function fileProcessing(file: File) {
    const { data: fileResponse } = await Api.uploadImage(file, {})
    const newSignature = {
      image: fileResponse.id,
      url: fileResponse.image,
    }
    setSignatures([...signatures, newSignature as SignatureType])
  }

  function deleteSignature(indexToDelete: number) {
    const updated = [...signatures]

    if (!updated[indexToDelete]?.id) {
      updated.splice(indexToDelete, 1)
      setSignatures(updated)
      return
    }

    updated[indexToDelete] = {
      ...updated[indexToDelete],
      deleted: true,
    }

    setSignatures(updated)
  }

  function updateSignature(index: number, updates: Partial<SignatureType>) {
    const updated = [...signatures]
    updated[index] = { ...updated[index], ...updates }
    setSignatures(updated)
  }

  React.useEffect(() => {
    const initialFields: React.ReactElement[] = []
    signatures.forEach((sig, idx) => {
      if (!sig.deleted && sig.image?.is_visible !== false) {
        initialFields.push(
          <SingleSignature
            key={sig.id || idx}
            signatureLink={{
              ...sig,
              url: sig.image?.url || sig.image?.image || sig.url,
            }}
            onDelete={() => deleteSignature(idx)}
            onUpdate={(updates) => updateSignature(idx, updates)}
          />,
        )
      }
    })
    setSignatureElementList(initialFields)
  }, [signatures])

  const handleSignatureSubmit = (file: File) => {
    fileProcessing(file).catch(console.error)
  }

  return (
    <section css={baseStyle}>
      {modalState}
      <b>
        <h3 className="add-signature" css={css({ color: variableColor })}>
          <AddSignatureIcon2
            onClick={() => {
              setModalState([
                <SignatureAndSketch
                  key="signature-modal"
                  hide={() => setModalState([])}
                  onSubmit={handleSignatureSubmit}
                />,
              ])
            }}
            height={'30px'}
            color={variableColor}
          />
        </h3>
      </b>
      <div className="signature-holder">{signatureElementList}</div>
    </section>
  )
}

const baseStyle = css({
  marginTop: 20,
  '.signature-holder': {
    display: 'flex',
    flexWrap: 'wrap',
  },
})
